import React, { useState, useMemo } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  Check, Monitor, Smartphone, Tablet, Package, Printer, CreditCard,
  UtensilsCrossed, MonitorSmartphone, Save, ArrowLeft, ArrowRight,
  ChevronDown, ChevronUp, Banknote, AlertCircle, Clock, Truck,
  MapPin, Cpu, Store, HandCoins, RefreshCw
} from 'lucide-react';

const peripheralCategories = [
  {
    id: 'pos_peripherals', name: 'POS Peripherals', icon: Printer,
    items: [
      { id: 'kitchen_printer', name: 'Kitchen Printer', price: 399 },
      { id: 'thermal_receipt_printer', name: 'Thermal Receipt Printer', price: 329 },
      { id: 'pinpad', name: 'Pinpad', price: 379 },
      { id: 'kitchen_display_14', name: 'Kitchen Display 14"', price: 699 },
      { id: 'kitchen_display_21', name: 'Kitchen Display 21"', price: 949 },
    ]
  },
  {
    id: 'self_order_kiosks', name: 'Self-Order Kiosks', icon: MonitorSmartphone,
    items: [
      { id: 'kiosk_15', name: 'Self-Order Kiosk 15.6"', price: 1499 },
      { id: 'kiosk_21', name: 'Self-Order Kiosk 21.5"', price: 1799 },
      { id: 'kiosk_wall', name: 'Wall-Mount Kiosk', price: 1399 },
    ]
  },
  {
    id: 'pay_at_table', name: 'Pay-at-Table Devices', icon: CreditCard,
    items: [
      { id: 'pay_table_handheld', name: 'Handheld Pay-at-Table', price: 549 },
      { id: 'pay_table_mini', name: 'Mini Pay-at-Table', price: 449 },
    ]
  },
  {
    id: 'qsr_pos', name: 'QSR POS Options', icon: UtensilsCrossed,
    items: [
      { id: 'qsr_drive_thru', name: 'Drive-Thru Terminal', price: 1099 },
      { id: 'qsr_bump_bar', name: 'Kitchen Bump Bar', price: 399 },
      { id: 'qsr_order_display', name: 'Order Confirmation Display', price: 799 },
    ]
  }
];

const platformProducts = {
  clover: [
    { value: 'clover_station_solo', name: 'Clover Station Solo', description: 'All-in-one countertop POS system', price: 2349, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/74qm3ajk_Clover%20Station%20Pro.jpg' },
    { value: 'clover_station_duo', name: 'Clover Station Duo', description: 'Dual-screen POS with customer-facing display', price: 2849, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/8uco5xr0_clover%20station%20duo.png' },
    { value: 'clover_mini', name: 'Clover Mini', description: 'Compact countertop terminal', price: 949, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/akyzynw0_clover%20mini%203%20gen.jfif' },
    { value: 'clover_flex', name: 'Clover Flex', description: 'Portable handheld with receipt printer', price: 699, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/6sy4ujz9_clover_flex_detail_2x.png' },
    { value: 'clover_compact', name: 'Clover Compact', description: 'Compact all-in-one terminal', price: 249, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/eszh10vp_Clover%20Stand%20Alone%20%281%29.png' }
  ],
  xerocharge: [
    { value: 'xerocharge_p_series', name: 'Xerocharge P-Series', description: 'Dual pricing handheld terminal', price: 299, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/zzsxykwh_Pseries%20Dual%20Price.png' },
    { value: 'xerocharge_qd1', name: 'Xerocharge QD1', description: 'Compact handheld with built-in printer', price: 249, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/0grg6ozx_QD1_1500-1-480x480.png' },
    { value: 'xerocharge_qd2', name: 'Xerocharge QD2', description: 'All-in-one mobile POS terminal', price: 349, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/dp2ztvkl_QD2_1500-1-480x480.png' },
    { value: 'xerocharge_qd4', name: 'Xerocharge QD4 Mobile', description: 'Smartphone tap-to-pay solution', price: 199, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/te04d5q9_QD4_1500.png' },
    { value: 'xerocharge_go', name: 'Xerocharge Go', description: 'Cellular handheld terminal', price: 179, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/kblu1k1o_XerochargeGo.jpg' }
  ],
  square: [
    { value: 'square_register', name: 'Square Register', description: 'All-in-one POS with customer display', price: 799, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/tx9qrby0_Square_Register__2nd_generation__undocked.png' },
    { value: 'square_terminal', name: 'Square Terminal', description: 'Portable card reader with receipt printer', price: 299, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/m5zffw1z_3867.jfif' },
    { value: 'square_stand', name: 'Square Stand', description: 'iPad POS stand with card reader', price: 149, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/k2jkcmse_PD07012_Square_Stand_Fallback-edited.webp' },
    { value: 'square_handheld', name: 'Square Handheld', description: 'Mobile card reader for on-the-go payments', price: 59, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/dym4ik2k_PD07019_USEN_Image-v2-mar-2025.webp' },
    { value: 'square_mobile_reader', name: 'Mobile Reader', description: 'Compact contactless card reader', price: 49, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/r6vctcm8_images.png' }
  ],
  exatouch: [
    { value: 'exatouch_salon_spa', name: 'Exatouch Salon & Spa', description: 'Complete POS system for salon and spa businesses', price: 999, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/de41100y_Exatouch-Salon-and-Spa-2.png' },
    { value: 'exatouch_restaurant', name: 'Exatouch Restaurant', description: 'Full-service restaurant POS solution', price: 1199, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/up2drond_products-exatouch-restaurant.png' },
    { value: 'exatouch_tablet', name: 'Exatouch Tablet', description: 'Portable tablet POS terminal', price: 599, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/47boamuh_products-tablet-front.png' },
    { value: 'exatouch_retail', name: 'Exatouch Retail', description: 'Complete retail POS system', price: 899, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/gn9yrtzx_RETAIL-exatouch-pos.png' },
    { value: 'exatouch_backoffice', name: 'Back Office Server', description: 'Centralized management and reporting system', price: 499, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/ovf30yg2_backofficeserver.png' }
  ],
  ingenico: [
    { value: 'ingenico_desk_1600', name: 'Ingenico Desk 1600', description: 'Compact countertop payment terminal', price: 229, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/cghp1lj8_Desk1600-face._0.png' },
    { value: 'ingenico_desk_3500', name: 'Ingenico Desk 3500', description: 'Advanced countertop POS terminal', price: 349, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/mn3scpv1_Desk3500-face..png' },
    { value: 'ingenico_lane_3000', name: 'Ingenico Lane 3000', description: 'Touchscreen countertop terminal', price: 449, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/n19xgpc3_lane3000-touch-3-4-square.png' },
    { value: 'ingenico_lane_8000', name: 'Ingenico Lane 8000', description: 'Premium all-in-one POS system', price: 799, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/uc2kswrm_laneserie-carrousel-8000.png' },
    { value: 'ingenico_move_5000', name: 'Move 5000 4G', description: 'Mobile payment terminal with 4G connectivity', price: 349, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/8o152wyx_Move5000-face.png' }
  ],
  payanywhere: [
    { value: 'payanywhere_a77', name: 'PayAnywhere A77', description: 'Smart mobile POS terminal', price: 199, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/k5568c95_A77_Slider1.png' },
    { value: 'payanywhere_a80_keypad', name: 'PayAnywhere A80 Keypad', description: 'Countertop terminal with integrated keypad', price: 249, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/jz3aym3s_a80.jpg' },
    { value: 'payanywhere_a80_pinpad', name: 'PayAnywhere A80 PinPad', description: 'Smart PIN pad with touchscreen', price: 179, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/uouf2jpr_newhardware-smartPinPad.png' },
    { value: 'payanywhere_a920', name: 'PayAnywhere A920', description: 'All-in-one smart terminal with receipt printer', price: 399, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/woxk79fw_hardware-smartTerminal-hero3.png' },
    { value: 'payanywhere_smart_flex', name: 'PayAnywhere Smart Flex', description: 'Portable wireless payment terminal', price: 299, image: 'https://customer-assets.emergentagent.com/job_processor-link/artifacts/n5h92xdg_hardware-slider-flex-2.png' }
  ]
};

const PROCESSORS = [
  { value: 'maverick', label: 'TSYS-msc', description: 'TSYS-msc Processing Network' },
  { value: 'cardconnect', label: 'FiservCopilot', description: 'FiservCopilot Processing Network' }
];

const BUSINESS_PROFILES = [
  { value: 'RETAIL', label: 'Retail' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'LODGING', label: 'Lodging' },
  { value: 'PETRO', label: 'Petro' },
  { value: 'MOTO', label: 'MOTO' },
  { value: 'ECOMM', label: 'E-Commerce' }
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

function Step4Equipment({ application, onNext, onPrevious, saving, onSaveDraft }) {
  const section = application.sections?.find(s => s.step_number === 4) || {};
  // Pull Step 1 business address for "use same" shortcut
  const step1Data = application.sections?.find(s => s.step_number === 1)?.data || {};

  const [formData, setFormData] = useState({
    // Processor & Business Profile
    processor: '',
    business_profile: '',
    batch_time: '',
    // Tips
    accepts_tips: '',
    tip_percentage: '',
    // Device & Software
    device_type: '',
    software_platform: '',
    software_only: false,
    starter_kit: '',
    peripherals: {},
    peripheral_quantities: {},
    // Shipping
    use_business_address: false,
    shipping_address: '',
    shipping_suite: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    // Billing Options
    billing_option: '',
    include_shipping_subscription: false,
    // TSYS/Maverick Boarding & Deployment Settings (Step 4, shown when processor === 'maverick')
    discount_period: '',
    funding_period: '',
    equipment_provided_by: '',
    equipment_type: '',
    equipment_used: '',
    equipment_note: '',
    batch_type: '',
    tip_setting: '',
    tip_percentages: '',
    ...section.data
  });

  const [expandedSections, setExpandedSections] = useState({ pos_peripherals: true });

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));

  const togglePeripheral = (id) => {
    setFormData(prev => ({
      ...prev,
      peripherals: { ...prev.peripherals, [id]: !prev.peripherals[id] },
      peripheral_quantities: { ...prev.peripheral_quantities, [id]: prev.peripheral_quantities[id] || 1 }
    }));
  };

  const setPeripheralQty = (id, qty) => {
    setFormData(prev => ({ ...prev, peripheral_quantities: { ...prev.peripheral_quantities, [id]: parseInt(qty) || 1 } }));
  };

  const handleUseBusinessAddress = (checked) => {
    set('use_business_address', checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        use_business_address: true,
        shipping_address: step1Data.business_address || '',
        shipping_suite: step1Data.business_suite || '',
        shipping_city: step1Data.business_city || '',
        shipping_state: step1Data.business_state || '',
        shipping_zip: step1Data.business_zip || ''
      }));
    }
  };

  const selectedProducts = formData.software_platform ? platformProducts[formData.software_platform] || [] : [];

  const totalEquipmentCost = useMemo(() => {
    if (formData.software_only) return 0;
    let total = 0;
    if (formData.starter_kit) {
      const product = selectedProducts.find(p => p.value === formData.starter_kit);
      if (product) total += product.price;
    }
    peripheralCategories.forEach(cat => {
      cat.items.forEach(item => {
        if (formData.peripherals[item.id]) total += item.price * (formData.peripheral_quantities[item.id] || 1);
      });
    });
    return total;
  }, [formData.starter_kit, formData.peripherals, formData.peripheral_quantities, selectedProducts, formData.software_only]);

  const monthlySubscription = useMemo(() => (totalEquipmentCost * 0.0544).toFixed(2), [totalEquipmentCost]);

  const deviceTypes = [
    { id: 'countertop', label: 'Countertop Terminal', icon: Monitor },
    { id: 'mobile', label: 'Mobile POS', icon: Smartphone },
    { id: 'tablet', label: 'Tablet POS', icon: Tablet }
  ];

  const showPaymentFields = formData.billing_option === 'merchant_ach' || formData.billing_option === 'merchant_cc' || formData.billing_option === 'agent_ach';
  const isFreePlace = formData.billing_option === 'free_placement';

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (isFreePlace) {
      submitData.ftp_override = true;
      submitData.ftp_rate = '5%';
    }
    onNext(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#3b82f6] flex items-center justify-center shrink-0">
          <Package className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" data-testid="step4-title">Equipment & Processing Setup</h3>
          <p className="text-sm text-muted-foreground">Configure processor, business profile, equipment, and billing</p>
        </div>
      </div>

      {/* Processor & Business Profile */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Cpu className="h-4 w-4 text-[#3b82f6]" />
          <span className="font-semibold text-sm">Processor & Business Profile</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm mb-1.5 block">Processor *</Label>
            <Select value={formData.processor} onValueChange={(v) => set('processor', v)}>
              <SelectTrigger data-testid="processor-select"><SelectValue placeholder="Select Processor" /></SelectTrigger>
              <SelectContent>
                {PROCESSORS.map(p => (
                  <SelectItem key={p.value} value={p.value}>
                    <div>
                      <div className="font-medium">{p.label}</div>
                      <div className="text-xs text-muted-foreground">{p.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Business Profile *</Label>
            <Select value={formData.business_profile} onValueChange={(v) => set('business_profile', v)}>
              <SelectTrigger data-testid="business-profile-select"><SelectValue placeholder="Select Business Profile" /></SelectTrigger>
              <SelectContent>
                {BUSINESS_PROFILES.map(bp => (
                  <SelectItem key={bp.value} value={bp.value}>{bp.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm mb-1.5 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-[#3b82f6]" /> Batch Time (24hr format) *
            </Label>
            <Input
              type="time"
              value={formData.batch_time}
              onChange={(e) => set('batch_time', e.target.value)}
              data-testid="batch-time-input"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">Time terminal batches daily transactions (e.g. 23:00)</p>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Batch Type</Label>
            <Select value={formData.batch_type} onValueChange={(v) => set('batch_type', v)}>
              <SelectTrigger data-testid="batch-type-select"><SelectValue placeholder="Select Batch Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Auto">Auto Close</SelectItem>
                <SelectItem value="Manual">Manual Close</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* TSYS Boarding & Deployment Settings — only relevant once TSYS/Maverick is the selected processor */}
      {formData.processor === 'maverick' && (
        <div className="border-2 border-[#3b82f6]/30 rounded-lg p-4 space-y-4 bg-blue-50/10 animate-in slide-in-from-top-2" data-testid="tsys-deployment-settings">
          <div className="flex items-center gap-2 mb-1">
            <Truck className="h-4 w-4 text-[#3b82f6]" />
            <span className="font-semibold text-sm">TSYS Boarding & Deployment Settings</span>
          </div>
          <p className="text-xs text-muted-foreground">Tells the boarding team the discount/funding period, equipment, and who's deploying it so they build the terminal file correctly.</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-sm mb-1.5 block">Discount Period</Label>
              <Select value={formData.discount_period} onValueChange={(v) => set('discount_period', v)}>
                <SelectTrigger data-testid="discount-period-select"><SelectValue placeholder="Select Discount Period" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Daily">Daily</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Funding Period</Label>
              <Select value={formData.funding_period} onValueChange={(v) => set('funding_period', v)}>
                <SelectTrigger data-testid="funding-period-select"><SelectValue placeholder="Select Funding Period" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SameDay">Same Day</SelectItem>
                  <SelectItem value="NextDay">Next Day</SelectItem>
                  <SelectItem value="TwoBusinessDays">Two Business Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Equipment Provided By</Label>
              <Select value={formData.equipment_provided_by} onValueChange={(v) => set('equipment_provided_by', v)}>
                <SelectTrigger data-testid="equipment-provided-by-select"><SelectValue placeholder="Who deploys the equipment?" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ISO">ISO (We Deploy)</SelectItem>
                  <SelectItem value="Deployment Center">Deployment Center (Drop-Ship)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Equipment Type</Label>
              <Select value={formData.equipment_type} onValueChange={(v) => set('equipment_type', v)}>
                <SelectTrigger data-testid="equipment-type-select"><SelectValue placeholder="Select Equipment Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_hardware">No Hardware (Stage Only)</SelectItem>
                  <SelectItem value="purchase">Terminal Purchase</SelectItem>
                  <SelectItem value="lease">Terminal Lease</SelectItem>
                  <SelectItem value="reprogram">Merchant-Owned Reprogram</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Equipment Used</Label>
              <Input
                value={formData.equipment_used}
                onChange={(e) => set('equipment_used', e.target.value)}
                placeholder="e.g. Ingenico Desk 3500"
                data-testid="equipment-used-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Equipment Note (for the boarding/deployment team)</Label>
            <textarea
              value={formData.equipment_note}
              onChange={(e) => set('equipment_note', e.target.value)}
              placeholder="e.g. Dual pricing 4%, needs Spanish receipt, ship to alt address"
              className="w-full min-h-[70px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="equipment-note-input"
            />
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <HandCoins className="h-4 w-4 text-[#3b82f6]" />
          <span className="font-semibold text-sm">Tips</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="text-sm mb-1.5 block">Tip Setting</Label>
            <Select value={formData.tip_setting} onValueChange={(v) => set('tip_setting', v)}>
              <SelectTrigger data-testid="tip-setting-select"><SelectValue placeholder="Select Tip Setting" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Tips</SelectItem>
                <SelectItem value="tip_line">Tip Line (printed on receipt)</SelectItem>
                <SelectItem value="tip_prompt">Tip Prompt (asked on terminal)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.tip_setting && formData.tip_setting !== 'none' && (
            <div>
              <Label className="text-sm mb-1.5 block">Suggested Tip Percentages</Label>
              <Input
                value={formData.tip_percentages}
                onChange={(e) => set('tip_percentages', e.target.value)}
                placeholder="e.g. 15,18,20"
                data-testid="tip-percentages-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated, sent to the deployment team for the terminal file</p>
            </div>
          )}
        </div>
      </div>

      {/* Device Type */}
      <div>
        <Label className="text-base font-semibold mb-3 block">Select Device Type</Label>
        <div className="grid gap-4 md:grid-cols-3">
          {deviceTypes.map(dt => {
            const isActive = formData.device_type === dt.id;
            const Icon = dt.icon;
            return (
              <div key={dt.id} onClick={() => set('device_type', dt.id)}
                className={`border-2 rounded-lg p-5 cursor-pointer transition-all text-center hover:shadow-sm ${isActive ? 'border-[#3b82f6] bg-blue-50/30' : 'border-border hover:border-[#3b82f6]/40'}`}
                data-testid={`device-type-${dt.id}`}>
                <Icon className={`h-8 w-8 mx-auto mb-2 ${isActive ? 'text-[#3b82f6]' : 'text-muted-foreground'}`} />
                <div className={`font-medium text-sm ${isActive ? 'text-[#3b82f6]' : ''}`}>{dt.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Software Provider + Products */}
      <div>
        <Label className="text-base font-semibold mb-3 block">Additional Options - Peripherals & Device Packages</Label>
        <div className="border rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-4 w-4 text-[#3b82f6]" />
            <span className="font-semibold text-sm">Software Provider</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">Select Software Provider</Label>
              <Select value={formData.software_platform} onValueChange={(v) => set('software_platform', v)}>
                <SelectTrigger data-testid="software-platform-select"><SelectValue placeholder="Choose Software Provider" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clover">CLOVER</SelectItem>
                  <SelectItem value="xerocharge">XEROCHARGE</SelectItem>
                  <SelectItem value="square">SQUARE</SelectItem>
                  <SelectItem value="exatouch">EXATOUCH</SelectItem>
                  <SelectItem value="ingenico">INGENICO</SelectItem>
                  <SelectItem value="payanywhere">PAYANYWHERE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox id="software-only" checked={formData.software_only} onCheckedChange={(v) => set('software_only', !!v)} data-testid="software-only-checkbox" />
              <Label htmlFor="software-only" className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap">Software Only - No Hardware Needed</Label>
            </div>
          </div>
        </div>

        {/* Software Only Reprogram Notice */}
        {formData.software_only && (
          <div className="border-2 border-indigo-300 rounded-xl p-4 bg-indigo-50 mb-4 animate-in slide-in-from-top-2" data-testid="software-only-reprogram-notice">
            <div className="flex items-start gap-2">
              <RefreshCw className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-sm text-indigo-800">Reprogram Only</h4>
                <p className="text-sm text-indigo-700 mt-1">
                  No hardware will be shipped. The existing terminal will be reprogrammed to the selected software provider.
                  All peripheral and device selections are disabled.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Product Cards */}
        {formData.software_platform && !formData.software_only && selectedProducts.length > 0 && (
          <div className="border rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Monitor className="h-4 w-4 text-[#3b82f6]" />
              <span className="font-semibold text-sm">POS Terminals</span>
            </div>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
              {selectedProducts.map((product) => {
                const isSelected = formData.starter_kit === product.value;
                return (
                  <div key={product.value} onClick={() => set('starter_kit', isSelected ? '' : product.value)}
                    className={`border-2 rounded-lg p-3 cursor-pointer transition-all relative hover:shadow-md ${isSelected ? 'border-[#3b82f6] bg-blue-50/30 shadow-lg' : 'border-border hover:border-[#3b82f6]/50'}`}
                    data-testid={`product-card-${product.value}`}>
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-[#3b82f6] text-white text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-muted/30 to-muted/10 rounded-lg p-2 overflow-hidden">
                        <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div>
                        <div className="font-semibold text-xs mb-0.5">{product.name}</div>
                        <div className="text-[10px] text-muted-foreground leading-relaxed">{product.description}</div>
                        <div className="font-bold text-sm text-[#3b82f6] mt-1">${product.price.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Collapsible Peripheral Sections */}
        {!formData.software_only && peripheralCategories.map(cat => {
          const isExpanded = expandedSections[cat.id];
          const Icon = cat.icon;
          const selectedCount = cat.items.filter(i => formData.peripherals[i.id]).length;
          return (
            <div key={cat.id} className="border rounded-lg mb-3 overflow-hidden">
              <button type="button" onClick={() => toggleSection(cat.id)} className="flex items-center justify-between w-full p-3 hover:bg-muted/30 transition-colors" data-testid={`toggle-${cat.id}`}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-[#3b82f6]" />
                  <span className="font-semibold text-sm">{cat.name}</span>
                  {selectedCount > 0 && <span className="text-[10px] bg-[#3b82f6] text-white px-1.5 py-0.5 rounded-full">{selectedCount}</span>}
                </div>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </button>
              {isExpanded && (
                <div className="border-t">
                  {cat.items.map(item => (
                    <div key={item.id} className="flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 hover:bg-muted/20">
                      <div className="flex items-center gap-3">
                        <Checkbox id={item.id} checked={!!formData.peripherals[item.id]} onCheckedChange={() => togglePeripheral(item.id)} data-testid={`peripheral-${item.id}`} />
                        <Label htmlFor={item.id} className="text-sm cursor-pointer">{item.name} <span className="font-bold">${item.price}</span></Label>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">Qty:</span>
                        <Select value={String(formData.peripheral_quantities[item.id] || 1)} onValueChange={(v) => setPeripheralQty(item.id, v)}>
                          <SelectTrigger className="w-16 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{[1,2,3,4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Shipping Section */}
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-[#3b82f6]" />
            <span className="font-semibold text-sm">Shipping Address</span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={!!formData.use_business_address}
              onCheckedChange={handleUseBusinessAddress}
              data-testid="use-business-address-checkbox"
            />
            <span className="text-sm text-muted-foreground">Use Business Address</span>
          </label>
        </div>
        <div>
          <Label className="flex items-center gap-1.5 mb-1.5">
            <MapPin className="h-3.5 w-3.5 text-[#3b82f6]" /> Shipping Address *
          </Label>
          <Input
            value={formData.shipping_address}
            onChange={(e) => set('shipping_address', e.target.value)}
            placeholder="Street address"
            className="mb-2"
            data-testid="shipping-address-input"
            disabled={formData.use_business_address}
          />
          <div className="grid gap-2 md:grid-cols-4">
            <Input
              value={formData.shipping_suite}
              onChange={(e) => set('shipping_suite', e.target.value)}
              placeholder="Suite/Apt#"
              data-testid="shipping-suite-input"
              disabled={formData.use_business_address}
            />
            <Input
              value={formData.shipping_city}
              onChange={(e) => set('shipping_city', e.target.value)}
              placeholder="City"
              data-testid="shipping-city-input"
              disabled={formData.use_business_address}
            />
            <Select
              value={formData.shipping_state}
              onValueChange={(v) => set('shipping_state', v)}
              disabled={formData.use_business_address}
            >
              <SelectTrigger data-testid="shipping-state-select"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                {US_STATES.map(st => (
                  <SelectItem key={st} value={st}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={formData.shipping_zip}
              onChange={(e) => set('shipping_zip', e.target.value)}
              placeholder="Zip Code"
              data-testid="shipping-zip-input"
              disabled={formData.use_business_address}
            />
          </div>
        </div>
      </div>

      {/* Billing Options */}
      <div>
        <Label className="text-base font-semibold mb-1 block">Billing Options</Label>
        <p className="text-sm text-muted-foreground mb-3">Select how this deal will be billed</p>
        <div className="grid gap-3 md:grid-cols-2">
          {[
            { id: 'merchant_ach', label: 'Merchant ACH', icon: Store },
            { id: 'merchant_cc', label: 'Merchant CC', icon: CreditCard },
            { id: 'agent_ach', label: 'Agent ACH', icon: HandCoins },
            { id: 'free_placement', label: 'Free Placement', note: 'Must enroll in 5% Cash Discount', icon: AlertCircle }
          ].map(opt => {
            const Icon = opt.icon;
            return (
              <div key={opt.id} onClick={() => set('billing_option', opt.id)}
                className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${formData.billing_option === opt.id ? 'border-[#3b82f6] bg-blue-50/30' : 'border-border hover:border-[#3b82f6]/40'}`}
                data-testid={`billing-${opt.id}`}>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${formData.billing_option === opt.id ? 'border-[#3b82f6]' : 'border-muted-foreground/40'}`}>
                  {formData.billing_option === opt.id && <div className="w-2 h-2 rounded-full bg-[#3b82f6]" />}
                </div>
                <Icon className={`h-4 w-4 shrink-0 ${formData.billing_option === opt.id ? 'text-[#3b82f6]' : 'text-muted-foreground'}`} />
                <div>
                  <span className="text-sm font-medium">{opt.label}</span>
                  {opt.note && <span className="text-xs text-muted-foreground ml-1">({opt.note})</span>}
                </div>
              </div>
            );
          })}
        </div>
        {/* Include shipping checkbox */}
        {formData.billing_option && formData.billing_option !== 'free_placement' && (
          <div className="mt-3 flex items-center gap-2">
            <Checkbox
              checked={!!formData.include_shipping_subscription}
              onCheckedChange={(v) => set('include_shipping_subscription', !!v)}
              data-testid="include-shipping-subscription"
            />
            <Label className="text-sm text-muted-foreground cursor-pointer">Include shipping cost in subscription billing</Label>
          </div>
        )}
      </div>

      {/* Billing Method Notice — no payment collection in the wizard (raw card/ACH capture removed; PCI scope + release checklist requirement) */}
      {showPaymentFields && (
        <div className="border rounded-lg p-4 space-y-2 bg-blue-50/20 animate-in slide-in-from-top-2" data-testid="billing-method-notice">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[#3b82f6]" />
            <h4 className="font-semibold text-sm">Billing Method Selected</h4>
          </div>
          <p className="text-xs text-muted-foreground">
            {formData.billing_option === 'merchant_cc' && 'Merchant will be billed by card. Payment details are collected securely outside the CRM at time of billing.'}
            {formData.billing_option === 'merchant_ach' && 'Merchant will be billed by ACH bank transfer. Bank details are collected securely outside the CRM at time of billing.'}
            {formData.billing_option === 'agent_ach' && 'Agent bank details will be collected securely outside the CRM for this billing arrangement.'}
          </p>
        </div>
      )}

      {/* Free Placement Auto-Override Notice */}
      {isFreePlace && (
        <div className="border-2 border-amber-300 rounded-xl p-4 bg-amber-50 animate-in slide-in-from-top-2" data-testid="free-placement-notice">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h4 className="font-semibold text-sm text-amber-800">FTP Cash Discount Applied</h4>
              <p className="text-sm text-amber-700 mt-1">
                By selecting Free Placement, the pricing program will automatically be set to <span className="font-bold">FTP Cash Discount at 5%</span>.
                This overrides any previously selected pricing program (Flat Rate, Cash Discount, or Interchange).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Total Equipment Cost */}
      {!formData.software_only && (
        <div className="bg-[#3b82f6] text-white rounded-xl px-5 py-4 flex items-center justify-between" data-testid="total-equipment-cost">
          <span className="font-semibold">Total Equipment Cost:</span>
          <span className="text-2xl font-bold">${totalEquipmentCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      )}

      {/* Subscription Monthly Payment */}
      {!formData.software_only && formData.billing_option !== 'free_placement' && totalEquipmentCost > 0 && (
        <div className="border-2 border-green-300 rounded-xl p-4 bg-green-50 animate-in slide-in-from-top-2" data-testid="subscription-payment">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="h-5 w-5 text-green-600" />
            <h4 className="font-semibold text-sm text-green-800">Monthly Subscription Estimate</h4>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-green-700">
              Equipment total <span className="font-semibold">${totalEquipmentCost.toLocaleString()}</span> x 0.0544 factor
            </div>
            <div className="text-2xl font-bold text-green-700">${monthlySubscription}/mo</div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onSaveDraft?.(formData)} data-testid="save-draft-btn">
          <Save className="h-4 w-4 mr-2" /> Save Draft
        </Button>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={onPrevious} data-testid="previous-step-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button type="submit" disabled={saving} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="next-step-btn">
            {saving ? 'Saving...' : 'Next Step'} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </form>
  );
}

export default Step4Equipment;
